"use client";

import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { DropdownMenu } from "@package/subframe/components/DropdownMenu";
import { IconButton } from "@package/subframe/components/IconButton";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { Table } from "@package/subframe/components/Table";
import {
  FeatherCalendarOff,
  FeatherMoreHorizontal,
  FeatherPencil,
  FeatherPlus,
  FeatherTrash,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import {
  useListClosuresQueryControllerList,
  useRemoveClosureCommandControllerRemove,
} from "@/generated/closures/closures";
import { clampPage, DEFAULT_PAGE_SIZE, totalPages } from "@/lib/pagination";

import {
  CLOSURE_TYPE_LABELS,
  ClosureFormDialog,
  ClosureFormValues,
} from "./ClosureFormDialog";

interface ClosuresTableProps {
  nurseryId: string;
  canManage: boolean;
}

/** Parse a date-only string as a civil day (avoid UTC→local off-by-one). */
function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatRange(start: string, end: string): string {
  return start === end
    ? formatDate(start)
    : `${formatDate(start)} – ${formatDate(end)}`;
}

export function ClosuresTable({ nurseryId, canManage }: ClosuresTableProps) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClosure, setEditingClosure] =
    useState<ClosureFormValues | null>(null);

  const {
    data: closuresData,
    isLoading,
    error,
  } = useListClosuresQueryControllerList(nurseryId);
  const closures = useMemo(() => closuresData?.data ?? [], [closuresData]);

  const [page, setPage] = useState(1); // 1-based, matches the Pagination component
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // The endpoint returns the full set, so the page is sliced client-side.
  // Removing the last closure on the final page shrinks the list, which would
  // leave `page` pointing past the end and the table empty.
  const safePage = clampPage(page, totalPages(closures.length, pageSize));
  const pagedClosures = useMemo(
    () => closures.slice((safePage - 1) * pageSize, safePage * pageSize),
    [closures, safePage, pageSize],
  );

  const { mutate: removeClosure } = useRemoveClosureCommandControllerRemove({
    mutation: {
      onSuccess: async () => {
        toast.success("Closure removed");
        await queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            typeof query.queryKey[0] === "string" &&
            query.queryKey[0].includes("/closures"),
          refetchType: "all",
        });
      },
      onError: () => {
        toast.error("Failed to remove closure");
      },
    },
  });

  const handleCreateClick = () => {
    setEditingClosure(null);
    setDialogOpen(true);
  };

  const handleEditClick = (closure: ClosureFormValues) => {
    setEditingClosure(closure);
    setDialogOpen(true);
  };

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setEditingClosure(null);
    }
  };

  return (
    <div className="flex w-full flex-col items-start gap-6">
      <div className="flex w-full flex-wrap items-center gap-4">
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Closures
          </span>
          <span className="text-body font-body text-subtext-color">
            Non-operating days (bank holidays, inset days, seasonal and ad-hoc
            closures). Scheduled items are not due, missed or overdue on these
            days.
          </span>
        </div>
        {canManage && (
          <Button
            variant="brand-primary"
            icon={<FeatherPlus />}
            onClick={handleCreateClick}
          >
            Add Closure
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex w-full items-center justify-center py-12">
          <Spinner className="h-8 w-8 text-primary" />
        </div>
      ) : error ? (
        <div className="flex w-full flex-col items-center justify-center py-12 gap-4">
          <IconWithLogoBackground variant="error" size="x-large" />
          <span className="text-body font-body text-subtext-color">
            Error loading closures. Please try again.
          </span>
        </div>
      ) : closures.length === 0 ? (
        <div className="flex w-full flex-col items-center justify-center py-12 gap-4 rounded-md border border-solid border-secondary-beige-200">
          <IconWithLogoBackground
            variant="neutral"
            size="x-large"
            icon={<FeatherCalendarOff />}
          />
          <div className="flex flex-col items-center gap-2">
            <span className="text-heading-3 font-heading-3 text-default-font">
              No closures yet
            </span>
            <span className="text-body font-body text-subtext-color text-center max-w-md">
              Add the days this nursery is closed so they don&apos;t count
              against completion or streaks.
            </span>
          </div>
          {canManage && (
            <Button
              variant="brand-primary"
              icon={<FeatherPlus />}
              onClick={handleCreateClick}
            >
              Add Closure
            </Button>
          )}
        </div>
      ) : (
        <div className="flex w-full flex-col items-start bg-default-background overflow-auto">
          <Table
            header={
              <Table.HeaderRow>
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>Dates</Table.HeaderCell>
                <Table.HeaderCell>Reason</Table.HeaderCell>
                {canManage && (
                  <Table.HeaderCell className="items-center justify-end">
                    Actions
                  </Table.HeaderCell>
                )}
              </Table.HeaderRow>
            }
          >
            {pagedClosures.map((closure) => (
              <Table.Row key={closure.id}>
                <Table.Cell>
                  <Badge variant="neutral">
                    {CLOSURE_TYPE_LABELS[closure.type]}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {formatRange(closure.startDate, closure.endDate)}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-body font-body text-subtext-color">
                    {closure.reason ?? "—"}
                  </span>
                </Table.Cell>
                {canManage && (
                  <Table.Cell variant="aligned-right">
                    <div className="flex items-center justify-end gap-2">
                      <SubframeCore.DropdownMenu.Root>
                        <SubframeCore.DropdownMenu.Trigger asChild={true}>
                          <IconButton
                            variant="neutral-primary"
                            size="medium"
                            icon={<FeatherMoreHorizontal />}
                          />
                        </SubframeCore.DropdownMenu.Trigger>
                        <SubframeCore.DropdownMenu.Portal>
                          <SubframeCore.DropdownMenu.Content
                            side="bottom"
                            align="end"
                            sideOffset={4}
                            asChild={true}
                          >
                            <DropdownMenu>
                              <DropdownMenu.DropdownItem
                                icon={<FeatherPencil />}
                                onClick={() =>
                                  handleEditClick({
                                    id: closure.id,
                                    type: closure.type,
                                    startDate: closure.startDate,
                                    endDate: closure.endDate,
                                    reason: closure.reason,
                                  })
                                }
                              >
                                Edit
                              </DropdownMenu.DropdownItem>
                              <DropdownMenu.DropdownItem
                                icon={<FeatherTrash />}
                                onClick={() =>
                                  removeClosure({
                                    nurseryId,
                                    closureId: closure.id,
                                  })
                                }
                              >
                                Remove
                              </DropdownMenu.DropdownItem>
                            </DropdownMenu>
                          </SubframeCore.DropdownMenu.Content>
                        </SubframeCore.DropdownMenu.Portal>
                      </SubframeCore.DropdownMenu.Root>
                    </div>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table>

          <Pagination
            className="pt-4"
            page={safePage}
            pageSize={pageSize}
            totalCount={closures.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            itemNoun="closures"
          />
        </div>
      )}

      <ClosureFormDialog
        nurseryId={nurseryId}
        closure={editingClosure}
        open={dialogOpen}
        onOpenChange={handleDialogClose}
      />
    </div>
  );
}
